import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Form, 
  Row, 
  Col, 
  Select, 
  Input, 
  InputNumber, 
  Radio, 
  DatePicker, 
  Divider, 
  Tag, 
  Button,
  Tooltip,
  Alert,
  Collapse
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

const { Option, OptGroup } = Select;
const { TextArea } = Input;
const { Panel } = Collapse;

// Inline constants to avoid import issues
const TREATMENT_OPTIONS = [
  { value: 'examination', label: '🔍 Examination', color: 'blue', common: true },
  { value: 'cleaning', label: '🧽 Prophylaxis/Cleaning', color: 'green', common: true },
  { value: 'filling', label: '🦷 Composite Filling', color: 'orange', common: true },
  { value: 'amalgamFilling', label: '⚫ Amalgam Filling', color: 'geekblue', common: true },
  { value: 'extraction', label: '🗑️ Extraction', color: 'red', common: false },
  { value: 'crown', label: '👑 Crown/Cap', color: 'purple', common: false },
  { value: 'rootCanal', label: '🔧 Root Canal Treatment', color: 'magenta', common: false },
  { value: 'bridge', label: '🌉 Bridge', color: 'cyan', common: false },
  { value: 'implant', label: '🔩 Dental Implant', color: 'gold', common: false },
  { value: 'veneer', label: '✨ Veneer', color: 'lime', common: false },
  { value: 'sealant', label: '🛡️ Pit & Fissure Sealant', color: 'volcano', common: true },
  { value: 'scaling', label: '🦷 Scaling & Root Planing', color: 'green', common: true }
];

const MATERIAL_OPTIONS = {
  filling: ['Composite Resin', 'Glass Ionomer', 'Amalgam', 'Gold Inlay'],
  crown: ['Porcelain', 'Metal Ceramic', 'Zirconia', 'Gold', 'Stainless Steel'],
  bridge: ['Porcelain', 'Metal Ceramic', 'Zirconia'],
  implant: ['Titanium', 'Zirconia']
};

// Inline utility functions
const getToothType = (toothNumber) => {
  const lastDigit = toothNumber % 10;
  if (lastDigit === 1 || lastDigit === 2) return 'incisor';
  if (lastDigit === 3) return 'canine';
  if (lastDigit === 4 || lastDigit === 5) return 'premolar';
  if (lastDigit === 6 || lastDigit === 7 || lastDigit === 8) return 'molar';
  return 'incisor';
};

const getQuadrant = (toothNumber) => {
  const firstDigit = Math.floor(toothNumber / 10);
  const quadrantNames = {
    1: 'Upper Right',
    2: 'Upper Left',
    3: 'Lower Left',
    4: 'Lower Right'
  };
  return quadrantNames[firstDigit] || 'Unknown';
};

// Cost estimation based on treatment type - moved outside component
const costEstimates = {
  examination: 500,
  cleaning: 1500,
  filling: 2500,
  amalgamFilling: 1800,
  extraction: 3000,
  crown: 15000,
  rootCanal: 8000,
  bridge: 25000,
  implant: 50000,
  veneer: 12000,
  sealant: 800,
  scaling: 2000
};

const TreatmentModal = ({ visible, onCancel, onSave, selectedTooth, chartData }) => {
  const [form] = Form.useForm();
  const [treatmentType, setTreatmentType] = useState('');
  const [setSelectedSurfaces] = useState([]);
  const [estimatedCost, setEstimatedCost] = useState(0);
  const [setShowAdvancedOptions] = useState(false);

  const toothType = getToothType(selectedTooth);
  const quadrant = getQuadrant(selectedTooth);
  const existingConditions = chartData[selectedTooth] || {};

  useEffect(() => {
    if (treatmentType && costEstimates[treatmentType]) {
      setEstimatedCost(costEstimates[treatmentType]);
      form.setFieldsValue({ cost: costEstimates[treatmentType] });
    }
  }, [treatmentType, form]);

  const handleSave = () => {
    form.validateFields().then(values => {
      onSave(selectedTooth, {
        ...values,
        toothType,
        quadrant,
        estimatedDuration: getDurationEstimate(values.treatmentType)
      });
      form.resetFields();
      setTreatmentType('');
      setSelectedSurfaces([]);
      setEstimatedCost(0);
    });
  };

  const getDurationEstimate = (type) => {
    const durations = {
      examination: 30,
      cleaning: 60,
      filling: 45,
      extraction: 30,
      crown: 90,
      rootCanal: 120,
      bridge: 150,
      implant: 180
    };
    return durations[type] || 30;
  };

  const commonTreatments = TREATMENT_OPTIONS.filter(t => t.common);
  const advancedTreatments = TREATMENT_OPTIONS.filter(t => !t.common);

  const getRecommendedTreatments = () => {
    const recommendations = [];
    if (existingConditions.decay) {
      recommendations.push('filling', 'crown');
    }
    if (existingConditions.pain) {
      recommendations.push('rootCanal', 'extraction');
    }
    return recommendations;
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '50px',
            height: '50px',
            backgroundColor: '#1890ff',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '18px'
          }}>
            {selectedTooth}
          </div>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
              Treatment for Tooth {selectedTooth}
            </div>
            <div style={{ fontSize: '13px', color: '#666', textTransform: 'capitalize' }}>
              {toothType} • {quadrant}
              {Object.keys(existingConditions).length > 0 && (
                <span style={{ marginLeft: '8px', color: '#fa8c16' }}>
                  • Existing conditions detected
                </span>
              )}
            </div>
          </div>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      onOk={handleSave}
      width={900}
      okText="💾 Save Treatment"
      cancelText="❌ Cancel"
      style={{ top: 20 }}
    >
      <Form form={form} layout="vertical">
        {/* Existing Conditions Alert */}
        {Object.keys(existingConditions).length > 0 && (
          <Alert
            message="Existing Conditions"
            description={
              <div>
                {Object.entries(existingConditions).map(([condition, value]) => 
                  value && (
                    <Tag key={condition} color="orange" style={{ marginBottom: '4px' }}>
                      {condition}
                    </Tag>
                  )
                )}
              </div>
            }
            type="warning"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        )}

        {/* Recommended Treatments */}
        {getRecommendedTreatments().length > 0 && (
          <Alert
            message="Recommended Treatments"
            description={
              <div>
                {getRecommendedTreatments().map(treatment => (
                  <Button
                    key={treatment}
                    size="small"
                    type="link"
                    onClick={() => {
                      setTreatmentType(treatment);
                      form.setFieldsValue({ treatmentType: treatment });
                    }}
                  >
                    {TREATMENT_OPTIONS.find(t => t.value === treatment)?.label}
                  </Button>
                ))}
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        )}

        <Divider orientation="left">
          <span style={{ fontSize: '16px', fontWeight: 'bold' }}>Treatment Information</span>
        </Divider>
        
        <Row gutter={16}>
          <Col span={16}>
            <Form.Item 
              name="treatmentType" 
              label="Treatment Type"
              rules={[{ required: true, message: 'Please select treatment type' }]}
            >
              <Select
                placeholder="Select treatment type"
                onChange={setTreatmentType}
                optionLabelProp="label"
                size="large"
              >
                <OptGroup label="🔧 Common Treatments">
                  {commonTreatments.map(option => (
                    <Option key={option.value} value={option.value} label={option.label}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Tag color={option.color}>{option.label}</Tag>
                        <span style={{ fontSize: '12px', color: '#666' }}>
                          ₹{costEstimates[option.value]?.toLocaleString()}
                        </span>
                      </div>
                    </Option>
                  ))}
                </OptGroup>
                <OptGroup label="⚕️ Advanced Treatments">
                  {advancedTreatments.map(option => (
                    <Option key={option.value} value={option.value} label={option.label}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Tag color={option.color}>{option.label}</Tag>
                        <span style={{ fontSize: '12px', color: '#666' }}>
                          ₹{costEstimates[option.value]?.toLocaleString()}
                        </span>
                      </div>
                    </Option>
                  ))}
                </OptGroup>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Estimated Cost">
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#f0f2f5',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#1890ff'
              }}>
                ₹{estimatedCost.toLocaleString()}
              </div>
            </Form.Item>
          </Col>
        </Row>

        {/* Material Selection */}
        {MATERIAL_OPTIONS[treatmentType] && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="material" label="Material Used">
                <Select placeholder="Select material" size="large">
                  {MATERIAL_OPTIONS[treatmentType].map(material => (
                    <Option key={material} value={material}>
                      {material}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="shade" label="Shade/Color">
                <Input placeholder="e.g., A2, B1, C3" size="large" />
              </Form.Item>
            </Col>
          </Row>
        )}

        {/* Surface Selection */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item 
              name="surface" 
              label={
                <span>
                  Surface Affected
                  <Tooltip title="Select all surfaces that require treatment">
                    <InfoCircleOutlined style={{ marginLeft: '4px', color: '#1890ff' }} />
                  </Tooltip>
                </span>
              }
            >
              <Select
                mode="multiple"
                placeholder="Select surfaces"
                onChange={setSelectedSurfaces}
                size="large"
              >
                <Option value="mesial">Mesial (M)</Option>
                <Option value="distal">Distal (D)</Option>
                <Option value="occlusal">Occlusal (O)</Option>
                <Option value="buccal">Buccal (B)</Option>
                <Option value="lingual">Lingual (L)</Option>
                <Option value="incisal">Incisal (I)</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="condition" label="Tooth Condition">
              <Select placeholder="Select condition" size="large">
                <Option value="healthy">✅ Healthy</Option>
                <Option value="decay">🦠 Dental Caries</Option>
                <Option value="fracture">💥 Fracture</Option>
                <Option value="worn">⚡ Attrition/Wear</Option>
                <Option value="sensitive">❄️ Sensitivity</Option>
                <Option value="pain">😣 Pain/Discomfort</Option>
                <Option value="abscess">🔴 Abscess</Option>
                <Option value="impacted">🚫 Impacted</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">
          <span style={{ fontSize: '16px', fontWeight: 'bold' }}>Clinical Details</span>
        </Divider>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="urgency" label="Treatment Urgency">
              <Select placeholder="Select urgency" size="large">
                <Option value="routine">🟢 Routine</Option>
                <Option value="urgent">🟡 Urgent</Option>
                <Option value="emergency">🔴 Emergency</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="anesthesia" label="Anesthesia Used">
              <Select placeholder="Select anesthesia" size="large">
                <Option value="none">None</Option>
                <Option value="topical">Topical</Option>
                <Option value="local">Local Injection</Option>
                <Option value="nitrous">Nitrous Oxide</Option>
                <Option value="sedation">IV Sedation</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="duration" label="Treatment Duration (min)">
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Minutes"
                min={5}
                max={300}
                step={5}
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="notes" label="Clinical Notes & Observations">
          <TextArea 
            rows={4} 
            placeholder="Enter detailed notes about the treatment, patient response, complications, materials used, technique, post-operative instructions, etc."
            size="large"
          />
        </Form.Item>

        {/* Advanced Options Collapse */}
        <Collapse 
          ghost 
          onChange={(keys) => setShowAdvancedOptions(keys.includes('advanced'))}
        >
          <Panel header="Advanced Options & Financial Details" key="advanced">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="cost" label="Actual Treatment Cost">
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Enter cost"
                    formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/₹\s?|(,*)/g, '')}
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="insurance" label="Insurance Coverage">
                  <Select placeholder="Insurance status" size="large">
                    <Option value="covered">✅ Fully Covered</Option>
                    <Option value="partial">⚠️ Partially Covered</Option>
                    <Option value="none">❌ Not Covered</Option>
                    <Option value="pending">⏳ Pending Approval</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="warranty" label="Warranty Period">
                  <Select placeholder="Warranty" size="large">
                    <Option value="none">No Warranty</Option>
                    <Option value="6months">6 Months</Option>
                    <Option value="1year">1 Year</Option>
                    <Option value="2years">2 Years</Option>
                    <Option value="5years">5 Years</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="followUp" label="Follow-up Required">
                  <Radio.Group size="large">
                    <Radio value={true}>✅ Yes</Radio>
                    <Radio value={false}>❌ No</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="followUpDate" label="Follow-up Date">
                  <DatePicker style={{ width: '100%' }} size="large" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="postOpInstructions" label="Post-Operative Instructions">
              <TextArea 
                rows={3} 
                placeholder="Special instructions for patient care, medications, dietary restrictions, activity limitations, etc."
                size="large"
              />
            </Form.Item>
          </Panel>
        </Collapse>
      </Form>
    </Modal>
  );
};

export default TreatmentModal;